import { useTheme } from "@emotion/react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useAssetSentinelData } from "../AssetSentinel/useAssetSentinelData";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { Box, Table, TableBody, TableCell, TableRow } from "@mui/material";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../components/Buttons/PATEditButton";
import { AMSModules, paginationStyle } from "../../utils/constants";
import { StatusBadge } from "../../utils/functions";
import { DataGridPro } from "@mui/x-data-grid-pro";
import WorkOrdersTableToolbar from "./WorkOrdersTableToolbar";
import { getRowsPerPageOptions } from "../../utils/functions";
import AddEditWorkOrderDialog from "./AddEditWorkOrderDialog";
import PATReopenButton from "../../components/Buttons/PATReopenButton";
import PATCloseButton from "../../components/Buttons/PATCloseButton";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
	make: [],
	model: [],
	year: [],
	department: [],
	employee: [],
	funding: [],
};

const WorkOrders = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();
	const { workOrders, vehicles, fundingSources, employees, codes } =
		useAssetSentinelData();
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);

	const total = workOrders?.length || 0;
	const loading = false;

	const [search, setSearch] = useState("");
	const [status, setStatus] = useState("");
	const [serviceFilter, setServiceFilter] = useState("");
	const [assignmentFilter, setAssignmentFilter] = useState("");
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo.workOrders || initialFilterState,
	);
	const [showModal, setShowModal] = useState(false);

	const currentAgency = useRef(null);

	const baseColumns = useMemo(
		() => [
			{
				field: "id",
				headerName: "WO#",
				width: 80,
				sortable: true,
			},
			{
				field: "vehicleID",
				headerName: "Vehicle",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <>{`${row?.vehicleID}`}</>;
				},
			},
			{
				field: "type",
				headerName: "Type",
				width: 150,
				sortable: true,
			},
			{
				field: "status",
				headerName: "Status",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <StatusBadge status={row?.status} />;
				},
			},
			{
				field: "assignedToID",
				headerName: "Assigned To",
				width: 150,
				sortable: true,
				// renderCell: ({ row }) => {
				// 	return <>{row?.odometer} mi</>;
				// },
			},
			{
				field: "vendor",
				headerName: "Vendor",
				width: 150,
				sortable: true,
			},
			{
				field: "openedDate",
				headerName: "Opened",
				width: 150,
				sortable: true,
			},
			{
				field: "closedDate",
				headerName: "Closed",
				width: 100,
				sortable: true,
			},
			{
				field: "downTime",
				headerName: "Down Time",
				width: 100,
				sortable: true,
			},
			{
				field: "cost",
				headerName: "Cost",
				width: 100,
				sortable: true,
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 150,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										{row?.status === "Open" ? (
											<PATCloseButton
												tooltipText="Close Work Order"
												// onClick={() =>
												// 	history.push(
												// 		`${PATH_APPLICANTS_EDIT}${row.id}`,
												// 	)
												// }
												size="20px"
											/>
										) : (
											<PATReopenButton
												tooltipText="Reopen Work Order"
												// onClick={() =>
												// 	history.push(
												// 		`${PATH_APPLICANTS_EDIT}${row.id}`,
												// 	)
												// }
												size="20px"
											/>
										)}
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Work Order"
											// onClick={() =>
											// 	history.push(
											// 		`${PATH_APPLICANTS_EDIT}${row.id}`,
											// 	)
											// }
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Work Order"
											// onClick={() => {
											// 	setSelectedId(row.id);
											// 	setOpenDeleteDialog(true);
											// }}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["workOrdersTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["workOrdersTableColumnOrder"]),
				);
			}

			if (settingsMap["workOrdersTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["workOrdersTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["workOrdersTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["workOrdersTableColumnVisibility"]),
				);
			}

			if (settingsMap["workOrdersTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["workOrdersTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			// Avoid unnecessary state updates if sort model didn't change
			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"workOrdersTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch();
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"workOrdersTableColumnOrder",
					JSON.stringify(newColumnOrder),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => {
			return {
				field: col.field,
				width: col.width,
			};
		});
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"workOrdersTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"workOrdersTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.WorkOrders, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.WorkOrders, payload: initialFilterState });
	};
	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 85px)", // or any other dynamic value subtracting top nav height
					marginTop: theme.spacing(0.5),
				}}
			>
				<WorkOrdersTableToolbar
					openAdd={() => setShowModal(true)}
					setSearch={setSearch}
					status={status}
					vehicles={vehicles}
					setStatus={setStatus}
					codes={codes}
					employees={employees}
					fundingSources={fundingSources}
					serviceFilter={serviceFilter}
					setServiceFilter={setServiceFilter}
					assignmentFilter={assignmentFilter}
					setAssignmentFilter={setAssignmentFilter}
					resetFilters={resetFilters}
					filters={filters}
					onFilterChange={handleOnFilterChange}
				/>

				<Box
					sx={{
						flex: 1,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<DataGridPro
						rows={workOrders}
						columns={columns}
						initialState={{ pinnedColumns: { right: ["actions"] } }}
						// getRowId={(row) => row.GUID}
						getRowId={(row) => row.id}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							handleColumnVisibilityModelChange
						}
						pageSize={pageSize}
						rowsPerPageOptions={rowsPerPageOptions}
						pagination
						paginationMode="server"
						sortModel={sortModel}
						sortingMode="server"
						onSortModelChange={handleSortModelChange}
						rowCount={total}
						page={page}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						loading={loading}
						disableSelectionOnClick
						onColumnOrderChange={handleColumnOrderChange}
						onColumnWidthChange={handleColumnWidthChange}
						sx={{
							...paginationStyle(theme, themeMode),
							color: theme.palette.colorFont,
							margin: 1,
						}}
					/>
				</Box>
			</Box>
			<AddEditWorkOrderDialog
				open={showModal}
				onClose={() => setShowModal(false)}
				onSave={() => undefined}
				editWO={false}
				viewOnly={false}
				vehicles={vehicles}
				workOrderTypes={codes?.workOrderTypes}
				employees={employees}
				fundingSources={fundingSources}
				codes={codes}
			/>
		</>
	);
};

export default WorkOrders;
